import { useState, useEffect, useMemo, useContext } from 'react';
import { Image } from '@imagekit/react';

import { DataGrid } from '@mui/x-data-grid';
import { ButtonGroup, Stack } from '@mui/material';
import Swal from 'sweetalert2';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';

import CustomSearch from '../components/custom_components/CustomSearch';
import CustomButton from '../components/custom_components/CustomButton';
import CustomRefreshButton from '../components/custom_components/CustomRefreshButton';
import CustomSelect from '../components/custom_components/CustomSelect';

import AddItem from '../components/manage_inventory/AddItem';
import EditItem from '../components/manage_inventory/EditItem';

import { CustomImage, PreviewImage } from '../utils/ImageKit';
import SearchNotFound from '../utils/SearchNotFound';
import { formatNumber } from '../utils/formatNumber';
import { UserContext } from '../context/UserContext';
import { useLockBodyScroll } from '../components/utils';

export default function ManageInventory() {

    const apiUrl = process.env.REACT_APP_API_URL;

    const { categories } = useContext(UserContext);

    const [data_rows, setRows] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [category, setCategory] = useState("");
    const [formattedCategories, setFormattedCategories] = useState("");

    const [isOpenAddItem, setIsOpenAddItem] = useState(false);
    const [isOpenEditItem, setIsOpenEditItem] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");

    useLockBodyScroll(isOpenAddItem)
    useLockBodyScroll(isOpenEditItem)
    useLockBodyScroll(previewOpen)

    // DATAGRID COLUMNS
    const columns = [
        {
            field: 'ItemImg',
            headerName: 'Item Preview',
            flex: 1.5,
            minWidth: 10,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <CustomImage
                    src={params.value}
                    width={35}
                    height={35}
                    onClick={() => {
                        setPreviewImage(params.value);
                        setPreviewOpen(true);
                    }}
                />
            )
        },
        {
            field: 'ItemID',
            headerName: 'Item ID',
            flex: 1.5,
            minWidth: 80
        },
        {
            field: 'ItemName',
            headerName: 'Item Name',
            flex: 1,
            minWidth: 300,
        },
        {
            field: 'Category',
            headerName: 'Category',
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'Price',
            headerName: 'Selling Price',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {formatNumber(params.value)}
                </div>
            ),
        },
        {
            field: 'Stock',
            headerName: 'Stock',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {formatNumber(params.value)}
                </div>
            ),
        },
    ];

    // INITIALIZED
    useEffect(() => {
        const formattedCategories = categories.map(item => ({
            label: item.ItemCategory,
            value: item.ItemCategory
        }));

        setFormattedCategories(formattedCategories);
    }, []);

    // DISPLAY ITEMS FUNCTION
    const handleDisplayItems = async (searchValue = '') => {
        try {
            setLoadingItems(true);
            setRows([]);

            const response = await fetch(
                `${apiUrl}manage_inventory/get_all_items.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        search: searchValue,
                        category: category
                    })
                });

            const result = await response.json();

            if (result.status === "success") {
                setRows(result.data || []);
            } else {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message || "Something went wrong!",
                    icon: 'error'
                });
            }

        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error.message || "Failed to fetch data",
                icon: 'error'
            });
            console.error("Server error:", error);
        } finally {
            setLoadingItems(false);
        }
    };

    useEffect(() => {
        handleDisplayItems(debouncedSearch);
    }, [debouncedSearch, category]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [search]);

    const selectedRowsData = useMemo(() => {
        return data_rows.filter(row =>
            selectedIds?.includes(row.id)
        );
    }, [data_rows, selectedIds]);

    // EDIT FUNCTION
    const handleEditItem = () => {
        if (selectedRowsData.length > 1) {
            Swal.fire({ text: "Please select a single item to edit!", icon: 'info' });
            return;
        }

        setIsOpenEditItem(true);
    }

    // DELETE FUNCTION
    const handleDelete = async () => {
        const isSingle = selectedRowsData.length === 1;

        const result = await Swal.fire({
            title: "Are you sure?",
            text: isSingle
                ? "This item will be deleted permanently!"
                : `${selectedRowsData.length} items will be deleted permanently!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it",
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        try {
            const ids = selectedRowsData.map(item => item.ItemID);

            const response = await fetch(`${apiUrl}manage_inventory/delete_item.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ ids }),
            });

            const data = await response.json();

            if (data.status === "success") {
                Swal.fire({
                    icon: "success",
                    text: data.message,
                });

                setRows(prev =>
                    prev.filter(row => !ids.includes(row.ItemID))
                );

                setSelectedIds([]);
            } else {
                Swal.fire({
                    icon: "error",
                    text: data.message,
                });
            }

        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                text: "Something went wrong",
            });
        }
    };

    return (
        <Stack direction="column" gap={2}>
            <Stack direction='row' justifyContent='space-between'>

                <Stack direction='row' gap={2}>
                    <CustomSearch
                        placeholder='Search by Item Name'
                        md='36ch'
                        value={search}
                        onChange={(val) => setSearch(val)}
                    />
                    <CustomRefreshButton
                        onClick={() => handleDisplayItems(debouncedSearch)}
                        loading={loadingItems}
                        label="Refresh"
                    />
                    <CustomSelect
                        label="Filter by Category"
                        value={category}
                        minWidth={180}
                        onChange={setCategory}
                        options={[
                            { label: <em>All</em>, value: "" },
                            ...(formattedCategories || [])
                        ]}
                    />
                </Stack>

                <ButtonGroup direction='row' gap={2}>
                    <CustomButton
                        label="Add"
                        startIcon={<AddBoxIcon />}
                        onClick={() => setIsOpenAddItem(true)}
                    />

                    <CustomButton
                        label="Edit"
                        disabled={selectedIds.length === 0}
                        startIcon={<EditIcon />}
                        onClick={handleEditItem}
                    />

                    <CustomButton
                        label="Delete"
                        disabled={selectedIds.length === 0}
                        startIcon={<DeleteIcon />}
                        onClick={handleDelete}
                    />
                </ButtonGroup>
            </Stack>

            <DataGrid
                autoHeight
                checkboxSelection
                rows={data_rows}   // ✅ FIXED
                columns={columns}
                loading={loadingItems} // ✅ nice UX
                localeText={{
                    noRowsLabel: loadingItems ? "" : <SearchNotFound />
                }}

                onRowSelectionModelChange={(newSelection) => {
                    let ids = [];

                    if (newSelection.type === 'include') {
                        // normal selection
                        ids = Array.from(newSelection.ids);
                    } else if (newSelection.type === 'exclude') {
                        // SELECT ALL → get all IDs except excluded
                        ids = data_rows
                            .filter(row => !newSelection.ids.has(row.id))
                            .map(row => row.id);
                    }

                    setSelectedIds(ids);
                }}

                getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
                initialState={{
                    pagination: { paginationModel: { pageSize: 20 } },
                }}
                pageSizeOptions={[10, 20, 50]}
                disableColumnResize
                disableColumnMenu
                disableColumnSorting
                disableRowSelectionOnClick
                density="compact"
                sx={{
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#1C2536',
                        borderBottom: 'none',
                    },

                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#1C2536',
                    },

                    '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#94A3B8',
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                    },

                    // ✅ REMOVE CLICK BORDER
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none',
                    },
                    // 🟢 NORMAL HOVER
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#dcfbff',
                        cursor: 'pointer',
                    },

                    // 🔵 SELECTED ROW
                    '& .MuiDataGrid-row.Mui-selected': {
                        backgroundColor: '#cfe8ff',
                    },

                    // 🔵 SELECTED + HOVER
                    '& .MuiDataGrid-row.Mui-selected:hover': {
                        backgroundColor: '#b6dcff',
                    },
                }}

                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                    },
                    filterPanel: {
                        filterFormProps: {
                            logicOperatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                            },
                            columnInputProps: {
                                variant: 'outlined',
                                size: 'small',
                                sx: { mt: 'auto' },
                            },
                            operatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                                sx: { mt: 'auto' },
                            },
                            valueInputProps: {
                                InputComponentProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                },
                            },
                        },
                    },
                }}
            />

            <AddItem
                open={isOpenAddItem}
                onClose={setIsOpenAddItem}
                onUpdate={(val) =>
                    setRows(prev => [
                        {
                            id: Date.now(),
                            ItemID: val.itemId,
                            ItemName: val.name,
                            Category: val.category,
                            Price: val.price,
                            Stock: val.stock,
                            ItemImg: val.image,
                        },
                        ...prev
                    ])
                }
            />

            <EditItem
                open={isOpenEditItem}
                onClose={setIsOpenEditItem}
                selectedItem={selectedRowsData[0] || {}}
                onUpdate={(val) => setRows(prev =>
                    prev.map(item =>
                        item.ItemID === val.itemId
                            ? {
                                ...item,
                                id: val.id,
                                ItemID: val.itemId,
                                ItemName: val.name,
                                Category: val.category,
                                Price: val.price,
                                Stock: val.stock,
                                ItemImg: val.image,
                            }
                            : item
                    )
                )}
            />

            <PreviewImage
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                src={previewImage}
            />
        </Stack>
    );
}
